import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ShoppingBag } from "lucide-react";
import { toast } from "sonner";
import ShopNav from "../../components/shop/ShopNav";
import { api, mediaUrl } from "../../lib/api";
import { useCart } from "../../context/CartContext";
import { useI18n } from "../../context/I18nContext";

const ease = [0.16, 1, 0.3, 1];
const money = (n) => `$${Number(n || 0).toLocaleString()}`;

export default function ShopProduct() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useI18n();
  const { add } = useCart();
  const [p, setP] = useState(null);

  useEffect(() => { api.get(`/shop/products/${id}`).then((r) => setP(r.data)).catch(() => {}); }, [id]);

  if (!p) return <div className="min-h-screen bg-cream"><ShopNav /><div className="max-w-6xl mx-auto px-6 py-20 text-navy/40">…</div></div>;

  return (
    <div className="min-h-screen bg-cream text-navy">
      <ShopNav />
      <div className="max-w-6xl mx-auto px-6 py-10">
        <Link to="/shop" className="inline-flex items-center gap-2 text-sm text-navy/50 hover:text-navy transition-colors mb-8"><ArrowLeft className="w-4 h-4" /> {t("shop.back")}</Link>
        <div className="grid md:grid-cols-2 gap-12">
          <motion.div initial={{ opacity: 0, scale: 0.98 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.6, ease }}
            className="aspect-[4/5] bg-white rounded-lg overflow-hidden border border-navy/10">
            <img src={mediaUrl(p.cover)} alt={p.name} className="w-full h-full object-cover" data-testid="shop-product-image" />
          </motion.div>
          <div className="flex flex-col justify-center">
            <div className="overline text-navy/40 mb-2">{p.category}</div>
            <h1 className="font-editorial text-4xl md:text-5xl tracking-tight">{p.name}</h1>
            <div className="text-2xl font-medium mt-4">{money(p.price)} <span className="text-navy/30 text-sm">MXN</span></div>
            {p.description && <p className="text-navy/60 leading-relaxed mt-6">{p.description}</p>}
            {p.materials?.length > 0 && (
              <div className="mt-6">
                <div className="overline text-navy/40 mb-2">{t("shop.materials")}</div>
                <div className="flex flex-wrap gap-2">{p.materials.map((m) => <span key={m} className="text-xs bg-white border border-navy/10 rounded-full px-3 py-1">{m}</span>)}</div>
              </div>
            )}
            <div className="flex flex-col sm:flex-row gap-3 mt-8">
              <button data-testid="shop-buy-now" onClick={() => { add(p, 1); navigate("/shop/cart"); }}
                className="inline-flex items-center justify-center gap-2 rounded-full bg-gradient-violet text-white px-6 py-3.5 hover:opacity-90 transition-opacity">
                {t("shop.buyNow")} <ShoppingBag className="w-4 h-4" />
              </button>
              <button data-testid="shop-add-cart" onClick={() => { add(p, 1); toast.success(t("shop.added")); }}
                className="inline-flex items-center justify-center gap-2 rounded-full border border-navy/15 px-6 py-3.5 hover:border-violet hover:text-violet transition-colors">
                {t("shop.addToCart")}
              </button>
            </div>
            <p className="text-xs text-navy/40 mt-4">{t("shop.shippingNote")}</p>
          </div>
        </div>
      </div>
    </div>
  );
}
